import { useLocation } from 'react-router-dom';
import routes from '../../router/config';

type FooterLink = { label: string; href: string; external?: boolean };
type FooterCategory = { heading: string; links: FooterLink[] };

// 言語切替は「現在ページの対訳URL」へ（Header の altLangPath と同型・router/config.tsx を正）。
const normPath = (p: string) => (p !== '/' ? p.replace(/\/+$/, '') : '/');
const ROUTE_SET = new Set(
  (routes as { path?: string }[])
    .map((r) => r.path || '')
    .filter((p) => p && !p.includes(':') && p !== '*')
    .map((p) => normPath(p))
);
const routeExists = (p: string) => ROUTE_SET.has(normPath(p));

function altLangPath(pathname: string, target: 'ja' | 'en'): string {
  const cur = normPath(pathname);
  const isEn = cur === '/en' || cur.startsWith('/en/');
  if (target === 'ja') {
    if (!isEn) return cur;
    return cur === '/en' ? '/' : cur.slice(3) || '/';
  }
  if (isEn) return cur;
  const en = cur === '/' ? '/en' : '/en' + cur;
  return routeExists(en) ? en : '/en';
}

export default function Footer() {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en');
  const currentYear = new Date().getFullYear();

  // ============================================================
  // 全ページリンクをカテゴリー別に掲載（v1包含・宙リンク0）。
  // 出典＝src/router/config.tsx の実ルート全量。JA=JAルート全量 / EN=ENルート全量。
  // 外部関連サイト（thebutler.jp / butlerbell.jp / butler-academy.jp）は external=true。
  // ============================================================
  const categoriesJA: FooterCategory[] = [
    {
      heading: '協会について',
      links: [
        { label: '協会について', href: '/about' },
        { label: '協会概要', href: '/about/overview' },
        { label: '理念と使命', href: '/about/mission' },
        { label: '沿革・歴史', href: '/about/history' },
        { label: '組織体制', href: '/about/organization' },
        { label: '主な活動', href: '/about/activities' },
        { label: 'アクセス・所在地', href: '/about/access' },
      ],
    },
    {
      heading: '執事サービス',
      links: [
        { label: '執事サービス一覧', href: '/butler-services/' },
        { label: 'フットマン', href: '/services/footman/' },
        { label: 'バトラー', href: '/services/butler/' },
        { label: 'セカンドスチュワード', href: '/services/second-steward/' },
        { label: 'ハウススチュワード', href: '/services/house-steward/' },
        { label: '執事とは', href: '/butler' },
        { label: '執事になるには', href: '/butler/become' },
        { label: '執事の実務基準', href: '/standards' },
        { label: '執事倫理綱領', href: '/standards/ethics' },
        { label: '執事行動規範', href: '/standards/conduct' },
        { label: '執事の認定基準', href: '/standards/certification' },
      ],
    },
    {
      heading: '資格制度（基本）',
      links: [
        { label: '資格制度一覧', href: '/qualifications' },
        { label: 'アソシエイトバトラー', href: '/qualifications/associate-butler' },
        { label: 'プロフェッショナルバトラー', href: '/qualifications/professional-butler' },
        { label: 'マスターバトラー', href: '/qualifications/master-butler' },
      ],
    },
    {
      heading: '資格制度（専門）',
      links: [
        { label: 'ビジネスマナー', href: '/qualifications/business-manner' },
        { label: 'ダイニングコーディネーター', href: '/qualifications/dining-coordinator' },
        { label: 'トラベルコンシェルジュ', href: '/qualifications/travel-concierge' },
        { label: 'ワイン・スチュワード', href: '/qualifications/wine-steward' },
        { label: 'イベントプランニング', href: '/qualifications/event-planning' },
        { label: 'ハウスホールドマネージャー', href: '/qualifications/household-manager' },
        { label: 'アフタヌーン・ティー', href: '/qualifications/afternoon-tea' },
        { label: 'ティー・ソムリエ', href: '/qualifications/tea-sommelier' },
        { label: 'バトラー・スタイル＆ユニフォーム', href: '/qualifications/butler-style-uniform' },
        { label: '通信講座', href: '/qualifications/distance-learning' },
      ],
    },
    {
      heading: 'セミナー・イベント',
      links: [
        { label: 'セミナー・イベント一覧', href: '/seminars' },
        { label: 'チームリーダーシップ研修', href: '/seminars/corporate/team-leadership' },
        { label: 'ホスピタリティ向上研修', href: '/seminars/corporate/hospitality-enhancement' },
        { label: 'コミュニケーション技術研修', href: '/seminars/corporate/communication-skills' },
        { label: 'スケジュール管理研修', href: '/seminars/corporate/schedule-management' },
        { label: '異文化理解研修', href: '/seminars/corporate/cross-cultural-understanding' },
        { label: '危機管理研修', href: '/seminars/corporate/crisis-management' },
      ],
    },
    {
      heading: 'ニュース・お知らせ',
      links: [
        { label: 'ニュース・お知らせ一覧', href: '/news' },
        { label: 'Raffles Sentosa 高級エスケープパッケージ', href: '/news/raffles-sentosa-butler-package' },
        { label: 'Raffles London「The Butler Did It」第2章', href: '/news/raffles-london-campaign' },
        { label: 'Downton Abbey: The Grand Finale 映画公開', href: '/news/downton-abbey-grand-finale' },
        { label: 'AIホスピタリティ革命', href: '/news/ai-hospitality-revolution' },
        { label: 'グラント・ハロルド王室バトラー回顧録出版', href: '/news/grant-harrold-royal-butler-memoir' },
        { label: 'Raffles グローバルキャンペーン「The Butler Did It」', href: '/news/raffles-butler-campaign' },
        { label: 'Apple社ロボットバトラー開発プロジェクト始動', href: '/news/apple-robot-butler' },
        { label: 'スイス超豪華旅行ブーム（スキーバトラー）', href: '/news/swiss-luxury-travel-ski-butler' },
        { label: '香港Butler AI 黒字化達成', href: '/news/hong-kong-butler-ai-profitability' },
        { label: 'TIBA 5年連続グローバルアワード受賞', href: '/news/tiba-global-award-five-years' },
        { label: '中国富裕層のバトラー需要急増', href: '/news/china-butler-demand-surge' },
        { label: 'Jumeirah Burj Al Arab 25周年記念', href: '/news/jumeirah-burj-al-arab-25th-anniversary' },
        { label: '世界的な執事不足で年収13万ドル超え', href: '/news/global-butler-shortage-salary-surge' },
        { label: 'Weave Robotics Isaac 家庭用ロボットバトラー発表', href: '/news/weave-robotics-isaac-robot-butler' },
      ],
    },
    {
      heading: '会員・お問い合わせ',
      links: [
        { label: '会員募集', href: '/membership' },
        { label: 'お問い合わせ', href: '/contact' },
      ],
    },
    {
      heading: '関連サイト',
      links: [
        { label: '関連サイト一覧', href: '/related' },
        { label: 'THE BUTLER（執事サービス）', href: '/related/the-butler' },
        { label: '執事ベル', href: '/related/butler-bell' },
        { label: '日本執事アカデミー', href: '/related/butler-academy' },
        { label: 'Queen\'s Butler（執事サロン）', href: '/related/queens-butler' },
        { label: 'THE BUTLER 公式サイト', href: 'https://thebutler.jp/', external: true },
        { label: '執事ベル 公式サイト', href: 'https://butlerbell.jp/', external: true },
        { label: '日本執事アカデミー 公式サイト', href: 'https://butler-academy.jp', external: true },
        { label: 'Queen\'s Butler 公式サイト', href: 'https://queensbutler.jp/', external: true },
      ],
    },
  ];

  const categoriesEN: FooterCategory[] = [
    {
      heading: 'About',
      links: [
        { label: 'About the Association', href: '/en/about/' },
        { label: 'Overview', href: '/en/about/overview/' },
        { label: 'Mission', href: '/en/about/mission/' },
        { label: 'History', href: '/en/about/history/' },
        { label: 'Organization', href: '/en/about/organization/' },
        { label: 'Activities', href: '/en/about/activities/' },
        { label: 'Access', href: '/en/about/access/' },
      ],
    },
    {
      heading: 'Butler Services',
      links: [
        { label: 'All Services', href: '/en/butler-services/' },
        { label: 'Footman', href: '/en/services/footman/' },
        { label: 'Butler', href: '/en/services/butler/' },
        { label: 'Second Steward', href: '/en/services/second-steward/' },
        { label: 'What Is a Butler?', href: '/en/butler' },
        { label: 'How to Become a Butler', href: '/en/butler/become' },
      ],
    },
    {
      heading: 'Certifications',
      links: [
        { label: 'All Certifications', href: '/en/qualifications/' },
        { label: 'Associate Butler', href: '/en/qualifications/associate-butler/' },
        { label: 'Professional Butler', href: '/en/qualifications/professional-butler/' },
        { label: 'Master Butler', href: '/en/qualifications/master-butler/' },
      ],
    },
    {
      heading: 'Seminars & Events',
      links: [
        { label: 'All Seminars', href: '/en/seminars/' },
        { label: 'Team Leadership', href: '/en/seminars/corporate/team-leadership/' },
        { label: 'Hospitality Enhancement', href: '/en/seminars/corporate/hospitality-enhancement/' },
        { label: 'Communication Skills', href: '/en/seminars/corporate/communication-skills/' },
        { label: 'Crisis Management', href: '/en/seminars/corporate/crisis-management/' },
        { label: 'Cross-Cultural Understanding', href: '/en/seminars/corporate/cross-cultural-understanding/' },
        { label: 'Schedule Management', href: '/en/seminars/corporate/schedule-management/' },
      ],
    },
    {
      heading: 'News & Updates',
      links: [
        { label: 'All News', href: '/en/news/' },
        { label: 'Raffles Sentosa Luxury Escape Package', href: '/en/news/raffles-sentosa-butler-package' },
        { label: 'Raffles London "The Butler Did It" Chapter 2', href: '/en/news/raffles-london-campaign' },
        { label: 'Downton Abbey: The Grand Finale', href: '/en/news/downton-abbey-grand-finale' },
        { label: 'AI Hospitality Revolution', href: '/en/news/ai-hospitality-revolution' },
        { label: 'Grant Harrold Royal Butler Memoir', href: '/en/news/grant-harrold-royal-butler-memoir' },
        { label: 'Raffles Global Campaign "The Butler Did It"', href: '/en/news/raffles-butler-campaign' },
        { label: 'Apple Robot Butler Development Project', href: '/en/news/apple-robot-butler' },
        { label: 'Swiss Ultra-Luxury Travel Boom', href: '/en/news/swiss-luxury-travel-ski-butler' },
        { label: 'Hong Kong Butler AI Profitability', href: '/en/news/hong-kong-butler-ai-profitability' },
        { label: 'TIBA Global Award, Fifth Consecutive Year', href: '/en/news/tiba-global-award-five-years' },
        { label: "China's Surge in Butler Demand", href: '/en/news/china-butler-demand-surge' },
        { label: 'Jumeirah Burj Al Arab 25th Anniversary', href: '/en/news/jumeirah-burj-al-arab-25th-anniversary' },
        { label: 'Global Butler Shortage Drives Salaries', href: '/en/news/global-butler-shortage-salary-surge' },
        { label: 'Weave Robotics Isaac Home Robot Butler', href: '/en/news/weave-robotics-isaac-robot-butler' },
      ],
    },
    {
      heading: 'Membership & Contact',
      links: [
        { label: 'Membership', href: '/en/membership/' },
        { label: 'Contact', href: '/en/contact/' },
      ],
    },
    {
      heading: 'Related Sites',
      links: [
        { label: 'All Related Sites', href: '/en/related/' },
        { label: 'THE BUTLER', href: '/en/related/the-butler/' },
        { label: 'Butler Bell', href: '/en/related/butler-bell/' },
        { label: 'Japan Butler Academy', href: '/en/related/butler-academy/' },
        { label: 'Queen\'s Butler', href: '/en/related/queens-butler/' },
        { label: 'THE BUTLER Official Site', href: 'https://thebutler.jp/', external: true },
        { label: 'Butler Bell Official Site', href: 'https://butlerbell.jp/', external: true },
        { label: 'Japan Butler Academy Official Site', href: 'https://butler-academy.jp', external: true },
        { label: 'Queen\'s Butler Official Site', href: 'https://queensbutler.jp/', external: true },
      ],
    },
  ];

  const categories = isEnglish ? categoriesEN : categoriesJA;

  const linkClass =
    'text-sm text-white/75 hover:text-[#d4af37] transition-colors duration-300 cursor-pointer leading-relaxed';

  return (
    <footer className="bg-[#1a2942] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        {/* ブランド情報 */}
        <div className="mb-12 pb-10 border-b border-white/15 grid lg:grid-cols-[1fr_auto] gap-8 items-start">
          <div>
            <a href={isEnglish ? '/en' : '/'} className="flex items-center space-x-3 mb-5 cursor-pointer group w-fit">
              <img src="/images/brand/jba-logo.png" alt="一般社団法人 全日本執事協会 ロゴ" className="h-10 w-10 object-contain shrink-0" />
              <h3
                className="text-lg font-bold group-hover:text-[#d4af37] transition-colors duration-300"
                style={{ fontFamily: '"Playfair Display", "Noto Serif JP", serif' }}
              >
                {isEnglish ? 'Japan Butler Association' : '一般社団法人全日本執事協会'}
              </h3>
            </a>
            <div className="space-y-1.5 text-sm text-white/70">
              <p>〒135-0064</p>
              <p>{isEnglish ? '2-7-4 Aomi, Koto-ku, Tokyo, Japan' : '東京都江東区青海2丁目7−4'}</p>
              <p>TEL: +81-3-6824-4086</p>
              <p>Email: info@butler-association.jp</p>
            </div>
          </div>

          {/* SNS */}
          <div className="flex space-x-4 lg:pt-2">
            <a
              href="https://www.instagram.com/japan.butler.association/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-white/60 hover:text-white transition-colors"
            >
              <i className="ri-instagram-line text-xl"></i>
            </a>
            <a
              href="https://x.com/japan_butler"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X (Twitter)"
              className="text-white/60 hover:text-white transition-colors"
            >
              <i className="ri-twitter-x-line text-xl"></i>
            </a>
            <a
              href="https://www.youtube.com/channel/UCa1P0s1FilnSM0MyeO1gRMA"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              className="text-white/60 hover:text-white transition-colors"
            >
              <i className="ri-youtube-line text-xl"></i>
            </a>
          </div>
        </div>

        {/* カテゴリー別 全ページリンク */}
        <nav
          aria-label={isEnglish ? 'Site map' : 'サイトマップ'}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-10"
        >
          {categories.map((cat) => (
            <div key={cat.heading}>
              <h4
                className="text-[13px] font-semibold tracking-[0.08em] text-[#d7b779] mb-4 pb-2 border-b border-[#a38e65]/30"
                style={{ fontFamily: '"Playfair Display", "Noto Serif JP", serif' }}
              >
                {cat.heading}
              </h4>
              <ul className="space-y-2">
                {cat.links.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      className={linkClass}
                      {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    >
                      {link.label}
                      {link.external && <i className="ri-external-link-line text-[10px] ml-1 align-middle opacity-70"></i>}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* フッター最下部 */}
        <div className="border-t border-white/15 mt-12 pt-8">
          {/* 言語切替 */}
          <div className="flex items-center justify-center space-x-3 mb-6">
            <span className="text-sm text-white/60">{isEnglish ? 'Language:' : '言語:'}</span>
            <a
              href={altLangPath(location.pathname, 'ja')}
              aria-current={!isEnglish ? 'true' : undefined}
              className={`text-sm font-medium transition-colors duration-300 ${
                !isEnglish ? 'text-[#a38e65] font-bold' : 'text-white/70 hover:text-white'
              }`}
            >
              日本語
            </a>
            <span className="text-white/40">|</span>
            <a
              href={altLangPath(location.pathname, 'en')}
              aria-current={isEnglish ? 'true' : undefined}
              className={`text-sm font-medium transition-colors duration-300 ${
                isEnglish ? 'text-[#a38e65] font-bold' : 'text-white/70 hover:text-white'
              }`}
            >
              English
            </a>
          </div>

          <div className="flex justify-center items-center text-center">
            <p className="text-sm text-white/60">
              {isEnglish
                ? `© ${currentYear} Japan Butler Association. All Rights Reserved.`
                : `© ${currentYear} 一般社団法人全日本執事協会 All Rights Reserved.`}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
